import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import type { RangeOption } from "../../constants/epicLeaderboard";
import { useApiResource } from "../../hooks/useApiResource";
import type { PkInsights, PkRankChange, PkRanks } from "../../types/playerPk";
import { playerApiUrl } from "../../utils/playerApi";
import PlayerPKSpreadChart from "../charts/PlayerPKSpreadChart";
import BestEpicTimes from "./BestEpicTimes";
import { PlayerLineStat } from "./PlayerLineStat";
import PlayerInsightsRow from "./PlayerInsightsRow";
import PlayerNumberCard from "./PlayerNumberCard";
import RankDelta from "./RankDelta";

ChartJS.register(ArcElement, Tooltip, Legend);

// Shape of GET public/player/:player (only the parts the Overview uses).
export type PlayerKillStats = {
  total: number;
  pk: number;
  hc: number;
  wanted: number;
};

export type PlayerStats = {
  kills: PlayerKillStats | null;
  deaths: PlayerKillStats | null;
  ranks?: { type: string; row_num: number }[];
  streak?: { length: number; type: string };
};

const styles = {
  title: {
    color: "rgb(70, 130, 180)",
    marginBottom: "16px",
    marginTop: "32px"
  }
};

const PREVIOUS_PERIOD_LABEL: Record<RangeOption, string> = {
  "7d": "the previous 7 days",
  "30d": "the previous 30 days",
  "90d": "the previous 90 days",
  year: "the previous period of the same length",
  all: ""
};

const getPrettifiedRankValue = (value: number | string | undefined) =>
  value && value !== "NA" ? `#${value}` : "NA";

type Props = {
  player: string;
  range: RangeOption;
  // ISO start of the range, undefined for all time.
  startDate: string | undefined;
  stats: PlayerStats;
};

const PlayerOverviewTab = ({ player, range, startDate, stats }: Props) => {
  const scope = player.toLowerCase();
  // Same URL as the PK tab's, so the response cache is shared between them.
  const insights = useApiResource<PkInsights>(
    useMemo(
      () => playerApiUrl(player, "pk-insights", { startDate }),
      [player, startDate]
    ),
    scope
  );
  // Rank movement only means something against a bounded range.
  const rankChange = useApiResource<PkRankChange>(
    useMemo(
      () =>
        startDate
          ? playerApiUrl(player, "pk-rank-change", { startDate })
          : null,
      [player, startDate]
    ),
    scope
  );

  const rank = (type: string) =>
    stats.ranks?.find((e) => e.type === type)?.row_num;
  const delta = (key: keyof PkRanks) => (
    <RankDelta rank={key} change={rankChange.data} />
  );
  const showDeltaNote = !!startDate && !!rankChange.data?.previous;

  return (
    <>
      <Row className="mt-5">
        <Col md={6}>
          {stats.kills !== null && (
            <PlayerPKSpreadChart data={[stats.kills.pk, stats.kills.hc]} />
          )}
        </Col>
        <Col
          md={6}
          className="mt-5 mt-md-0 d-flex flex-column justify-content-center align-items-end"
        >
          <PlayerLineStat
            name="Rank"
            tooltip="All-time rank by weighted points: 5 per PK kill + 5 per HC kill + 2.5 per assist. Ties broken alphabetically."
            value={getPrettifiedRankValue(rank("points"))}
            extra={delta("points")}
          />
          <PlayerLineStat
            name="PK Rank"
            tooltip="All-time rank by total PK kills. Ties broken alphabetically."
            value={getPrettifiedRankValue(rank("pk"))}
            extra={delta("pk")}
          />
          <PlayerLineStat
            name="HC Rank"
            tooltip="All-time rank by total HC kills. Ties broken alphabetically."
            value={getPrettifiedRankValue(rank("hc"))}
            extra={delta("hc")}
          />
          <PlayerLineStat
            name="Assist Rank"
            tooltip="All-time rank by total assists. Ties broken alphabetically."
            value={getPrettifiedRankValue(rank("assists"))}
            extra={delta("assists")}
          />
          <PlayerLineStat
            name="Streak"
            value={stats.streak}
            type="streak"
            extra={<span className="rank-delta" />}
          />
          {showDeltaNote && (
            <div className="player-muted-note mt-1">
              Arrows compare with {PREVIOUS_PERIOD_LABEL[range]}
            </div>
          )}
          <div className="player-muted-note mt-1">
            Streak is current (not affected by the date range)
          </div>
        </Col>
      </Row>

      <PlayerInsightsRow insights={insights} range={range} />

      {stats.kills !== null && (
        <Row>
          <h3 style={styles.title}>KILLS</h3>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="total" number={stats.kills.total} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="opk" number={stats.kills.pk} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="hc" number={stats.kills.hc} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="wanted" number={stats.kills.wanted} />
          </Col>
        </Row>
      )}

      {stats.deaths !== null && (
        <Row>
          <h3 style={styles.title}>DEATHS</h3>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="total" number={stats.deaths.total} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="opk" number={stats.deaths.pk} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="hc" number={stats.deaths.hc} />
          </Col>
          <Col md={3} xs={6}>
            <PlayerNumberCard title="wanted" number={stats.deaths.wanted} />
          </Col>
        </Row>
      )}

      <BestEpicTimes player={player} range={range} />
    </>
  );
};

export default PlayerOverviewTab;
